/**
 * Configuration for Returns Auto Submission feature
 *
 * This configuration maps each return type to the entitlement that unlocks it, the feature flag
 * that gates its release, and the countries it is available in. Direct Submission is a paid
 * add-on, so the tenant must hold the IAM entitlement (resolved by the BFF) - and the feature
 * flag is retained on top of it as a per-environment kill switch we control without IAM.
 */

import { FilingTypeEnum, FeatureFlagEnum } from './enums';
import { ENTITLEMENT_FEATURES, hasEntitlement } from './entitlements';

/**
 * Configuration for auto submission feature by return type
 * Maps each return type to its corresponding feature flag, entitlement and enabled countries
 *
 * To add auto-submission for a new return type:
 * 1. Add the new feature to Iam
 * 2. Add its name to ENTITLEMENT_FEATURES in entitlements.js
 * 3. Add the feature flag to FeatureFlagEnum in enums.js
 * 4. Add the configuration here
 * 5. Pass doInitiateAutoSubmission prop to the return type's <PrepareFile /> component
 */
export const AUTO_SUBMISSION_CONFIG = Object.freeze({
  [FilingTypeEnum.VATRETURN]: {
    featureFlag: FeatureFlagEnum.ENABLE_VAT_RETURN_AUTO_SUBMISSION,
    entitlement: ENTITLEMENT_FEATURES.DIRECT_SUBMISSION,
    enabledCountries: ['FR'] // France is the initial country for VAT Return auto-submission
  },
  // Future return types can be added here:
  // [FilingTypeEnum.ECLISTING]: {
  //   featureFlag: 'EnableECListingAutoSubmission',
  //   entitlement: ENTITLEMENT_FEATURES.EC_LISTING,
  //   enabledCountries: ['FR', 'IT']
  // },
});

/**
 * Checks if auto submission is enabled for a specific return type and country
 * @param {string} filingTypeId - The return type (e.g., 'VATReturn', 'ECListing')
 * @param {string} countryCode - The country code (e.g., 'FR', 'IT')
 * @param {object} entitlements - The entitlements sub-state, i.e. state.authentication.entitlements
 * @param {object} featureFlags - The feature flags object from Redux state
 * @returns {boolean} True if auto submission is enabled for this return type and country
 */
export const isAutoSubmissionEnabled = (filingTypeId, countryCode, entitlements, featureFlags) => {
  if (!filingTypeId || !countryCode || !entitlements || !featureFlags) {
    return false;
  }

  // Get configuration for this return type
  const config = AUTO_SUBMISSION_CONFIG[filingTypeId];
  if (!config) {
    return false; // No auto submission configured for this return type
  }

  // Check if country is enabled for this return type
  const isCountryEnabled = config.enabledCountries.includes(countryCode.toUpperCase());
  if (!isCountryEnabled) {
    return false;
  }

  // Kill switch: lets us turn the feature off per environment without an IAM change
  if (featureFlags[config.featureFlag] !== true) {
    return false;
  }

  // Check the tenant has purchased the feature
  return hasEntitlement(entitlements, config.entitlement);
};

export const getDirectSubmissionEnabledCountries = () => [
  ...new Set(
    Object.values(AUTO_SUBMISSION_CONFIG)
      .flatMap(config => config.enabledCountries || [])
      .map(countryCode => countryCode.toUpperCase())
  )
];

export default {
  AUTO_SUBMISSION_CONFIG,
  isAutoSubmissionEnabled,
  getDirectSubmissionEnabledCountries
};
